import * as Sentry from '@sentry/react';

/**
 * Client-entry side effect. Browser translators (e.g. Chrome's built-in Google
 * Translate) replace the text nodes React rendered with their own <font>
 * elements. React still holds the original nodes, so its next removeChild /
 * insertBefore on them throws NotFoundError and unmounts the whole app. Skip
 * those removals and append those inserts instead of crashing; the cost is
 * possibly stale or misplaced content on screen.
 */

const { removeChild, insertBefore } = Node.prototype;

let reported = false;

const reportMismatch = (message: string, child: Node, parent: Node) => {
  console.error(message, child.nodeName, parent.nodeName);
  if (reported) return;
  reported = true;
  const { classList } = document.documentElement;
  Sentry.captureMessage(message, {
    level: 'warning',
    tags: { translated: classList.contains('translated-ltr') || classList.contains('translated-rtl') },
    extra: { child: child.nodeName, parent: parent.nodeName },
  });
};

Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
  if (child.parentNode !== this) {
    reportMismatch('Skipped removeChild: node is not a child of this parent', child, this);
    return child;
  }
  return removeChild.call(this, child) as T;
};

Node.prototype.insertBefore = function <T extends Node>(this: Node, node: T, child: Node | null): T {
  if (child && child.parentNode !== this) {
    reportMismatch('Appended instead of insertBefore: reference node is not a child of this parent', child, this);
    return insertBefore.call(this, node, null) as T;
  }
  return insertBefore.call(this, node, child) as T;
};

export {};
